import React, { useState, useEffect } from "react";
import { Activity, AlertTriangle, ListChecks, Layers, GitBranch, PieChart, BarChart2, TrendingUp } from "lucide-react";
import FileAnalyticsAPI from "../file_analytics/fileAnalyticsService";
import Toast, { type ToastMessage } from "../common/Toast";
import { MonitoringInsightsSkeleton } from "../common/InsightsSkeleton";
import NoDataFound from "../common/NoDataFound";
import { usePermission } from "../../contexts/PermissionContext";

const ACT_INSIGHTS = "CMG_REPORTS";

interface PipelineMetric { collection_type_id: number; pipeline_name: string; runs: number; anomalies: number; }
interface RuleMetric { rule_id: number; rule_name: string; anomalies: number; }
interface StatusMetric { status: string; count: number; }
interface Count { label: string; count: number; }

interface Metrics {
  total_runs: number;
  total_anomalies: number;
  open_anomalies: number;
  active_rules: number;
  by_pipeline: PipelineMetric[];
  by_rule?: RuleMetric[];
  by_status: StatusMetric[];
  rules_by_status: Count[];
  rules_by_severity: Count[];
  runs_by_status: Count[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

const PALETTE_COLORS = ['#8b5cf6','#6366f1','#3b82f6','#06b6d4','#10b981','#f59e0b','#f43f5e'];
const SEVERITY_COLOR: Record<string, string> = {
  Critical: '#ef4444', High: '#f97316', Medium: '#f59e0b', Low: '#10b981',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, icon: Icon, bg, fg }: any) => (
  <div className="bg-white px-5 py-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow">
    <div className={`p-2.5 rounded-xl shrink-0 ${bg}`}>
      <Icon size={20} className={fg} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider truncate">{label}</p>
      <span className="text-3xl font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
      {sub && <p className="text-[11px] text-slate-400 mt-1 truncate">{sub}</p>}
    </div>
  </div>
);

const DistributionWidget = ({ title, data, icon: Icon, total, className = '' }: any) => {
  const maxCount = Math.max(...data.map((d: any) => d.count), 1);
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col ${className}`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className="text-indigo-400 shrink-0" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</span>
        </div>
        {total > 0 && <span className="text-xs font-bold text-slate-400">{total} total</span>}
      </div>
      {data.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6 italic">No data.</p>
      ) : (
        <ul className="divide-y divide-slate-50 flex-1">
          {data.map((item: any, idx: number) => {
            const color = item.color || PALETTE_COLORS[idx % PALETTE_COLORS.length];
            const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
            const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
            return (
              <li key={idx} className="grid items-center gap-3 px-4 py-2 hover:bg-slate-50/70 transition-colors"
                style={{ gridTemplateColumns: '1fr 120px 4rem' }}>
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide truncate">
                  {item.label}
                </span>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-1.5 rounded-full transition-all duration-500"
                    style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: color }} />
                </div>
                <span className="text-[11px] font-bold text-right tabular-nums"
                  style={{ color: item.count > 0 ? color : '#94a3b8' }}>
                  {item.count} <span className="font-normal text-slate-400">({pct}%)</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

const AnalyticsTab: React.FC<{ openRisksCount?: number }> = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const { allowed: canView, loading: viewLoading } = usePermission(ACT_INSIGHTS, 'view');

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    FileAnalyticsAPI.listMetrics()
      .then((data) => { if (!cancelled) setMetrics(data); })
      .catch(() => { if (!cancelled) setToast({ type: 'error', message: 'Could not load monitoring metrics.' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canView]);

  const totalAnom = metrics?.total_anomalies ?? 0;
  const fmt = (n?: number) => loading ? '—' : String(n ?? 0);

  const pipelineData = (metrics?.by_pipeline ?? [])
    .filter(p => p.anomalies > 0)
    .map((p, i) => ({ label: p.pipeline_name, count: p.anomalies, color: PALETTE_COLORS[i % PALETTE_COLORS.length] }));

  const ruleData = (metrics?.by_rule ?? [])
    .filter(r => r.anomalies > 0)
    .map((r, i) => ({ label: r.rule_name, count: r.anomalies, color: PALETTE_COLORS[i % PALETTE_COLORS.length] }));

  const statusData = (metrics?.by_status ?? [])
    .map((s, i) => ({ label: s.status, count: s.count, color: PALETTE_COLORS[i % PALETTE_COLORS.length] }));

  const severityData = (metrics?.rules_by_severity ?? [])
    .map(s => ({ label: s.label, count: s.count, color: SEVERITY_COLOR[s.label] || '#94a3b8' }));

  const ruleStatusData = (metrics?.rules_by_status ?? [])
    .map((s, i) => ({ label: s.label, count: s.count, color: PALETTE_COLORS[i % PALETTE_COLORS.length] }));

  const runStatusData = (metrics?.runs_by_status ?? [])
    .map((s, i) => ({ label: s.label, count: s.count, color: PALETTE_COLORS[i % PALETTE_COLORS.length] }));

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to monitoring insights." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">
      <div className="netbody-content">
        <div className="scroll-container-nofilters flex-1 overflow-hidden">

          {loading && <MonitoringInsightsSkeleton />}

          {!loading && (
            <div className="py-4 space-y-4">

              {/* KPI cards — 3 cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <KpiCard label="Total Runs" value={fmt(metrics?.total_runs)}
                  sub="Pipeline rule runs" icon={Activity} bg="bg-blue-50" fg="text-blue-600" />
                <KpiCard label="Total Anomalies" value={fmt(metrics?.total_anomalies)}
                  sub="Across all pipelines" icon={AlertTriangle} bg="bg-rose-50" fg="text-rose-500" />
                <KpiCard label="Open for Triage" value={fmt(metrics?.open_anomalies)}
                  sub="New or under review" icon={ListChecks} bg="bg-amber-50" fg="text-amber-500" />
              </div>

              {/* Anomalies by Pipeline + Rule */}
              <div className="grid grid-cols-2 gap-3">
                <DistributionWidget title="Anomalies by Pipeline" data={pipelineData} icon={Layers} total={totalAnom} />
                <DistributionWidget title="Anomalies by Rule" data={ruleData} icon={GitBranch} total={totalAnom} />
              </div>

              {/* Status + Rules + Runs */}
              <div className="grid grid-cols-3 gap-3">
                <DistributionWidget title="Anomalies by Status" data={statusData} icon={PieChart} total={totalAnom} />
                <DistributionWidget title="Rules by Severity" data={severityData} icon={BarChart2}
                  total={severityData.reduce((s, d) => s + d.count, 0)} />
                <DistributionWidget title="Rules by Status" data={ruleStatusData} icon={TrendingUp}
                  total={ruleStatusData.reduce((s, d) => s + d.count, 0)} />
              </div>

            </div>
          )}

        </div>
      </div>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default AnalyticsTab;
